import React, { useEffect, useState } from "react";
import {
  Modal, Form, Button, Dropdown, Input, Select, Switch,
  InputNumber, Tag, message,
} from "antd";
import {
  MedicineBoxOutlined,
  BulbOutlined,
  FileTextOutlined,
  StarOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  PoweroffOutlined,
  MoreOutlined,
} from "@ant-design/icons";

import {
  getGuidanceApi,
  createGuidanceApi,
  updateGuidanceApi,
  deleteGuidanceApi,
} from "../../services/api";
import AppTable from "../../common/AppTable";
import AppInput from "../../common/FormInputs/AppInput";
import AppSelect from "../../common/FormInputs/AppSelect";

const CATEGORIES = [
  { value: "Doctor Guidance",   label: "Doctor Guidance",   icon: <MedicineBoxOutlined />, color: "#6366F1" },
  { value: "Parenting Tips",    label: "Parenting Tips",    icon: <BulbOutlined />,        color: "#F59E0B" },
  { value: "Instructions",      label: "Instructions",      icon: <FileTextOutlined />,    color: "#10B981" },
  { value: "Thought of the Day",label: "Thought of the Day",icon: <StarOutlined />,        color: "#EC4899" },
];

const AGE_GROUPS = [
  { value: "0-3",    label: "0–3 Months (Newborn)"   },
  { value: "3-6",    label: "3–6 Months (Infant)"    },
  { value: "6-12",   label: "6–12 Months (Infant)"   },
  { value: "1-2yr",  label: "1–2 Years (Toddler)"    },
  { value: "2-5yr",  label: "2–5 Years (Preschool)"  },
  { value: "5-10yr", label: "5–10 Years (School Age)" },
];

const ACCENT = "#534ab7";

export default function Guidance() {
  const [allItems,  setAllItems]  = useState([]);
  const [activeTab, setActiveTab] = useState("Doctor Guidance");
  const [search,    setSearch]    = useState("");
  const [open,      setOpen]      = useState(false);
  const [editing,   setEditing]   = useState(null);
  const [loading,   setLoading]   = useState(false);
  const [form] = Form.useForm();
  const watchCategory = Form.useWatch("category", form);

  // ─── fetch ────────────────────────────────────────────────────
  const fetchAll = async () => {
    try {
      const res = await getGuidanceApi();
      setAllItems((res.data.data || []).slice().sort((a, b) => b.id - a.id));
    } catch {
      message.error("Failed to load guidance content");
    }
  };

  useEffect(() => { fetchAll(); }, []);

  // ─── derived list ─────────────────────────────────────────────
  const displayed = allItems
    .filter((i) => i.category === activeTab)
    .filter((i) =>
      !search ||
      i.title?.toLowerCase().includes(search.toLowerCase()) ||
      i.body?.toLowerCase().includes(search.toLowerCase())
    );

  // ─── counts per tab ───────────────────────────────────────────
  const countFor = (cat) => allItems.filter((i) => i.category === cat).length;

  // ─── open modal ───────────────────────────────────────────────
  const openAdd = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ category: activeTab, active: true, sortOrder: 0 });
    setOpen(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    form.setFieldsValue({
      category:        record.category,
      title:           record.title,
      ageGroup:        record.ageGroup,
      ageRange:        record.ageRange,
      body:            record.body,
      sortOrder:       record.sortOrder,
      active:          record.active,
      doctorName:      record.doctorName || "",
      doctorSpeciality:record.doctorSpeciality || "",
      doctorPhotoUrl:  record.doctorPhotoUrl || "",
    });
    setOpen(true);
  };

  // ─── submit ───────────────────────────────────────────────────
  const handleSubmit = async () => {
    const values = await form.validateFields();
    setLoading(true);
    try {
      if (editing) {
        await updateGuidanceApi(editing.id, values);
        message.success("Content updated");
      } else {
        await createGuidanceApi(values);
        message.success("Content added");
      }
      setOpen(false);
      fetchAll();
    } catch {
      message.error("Failed to save content");
    } finally {
      setLoading(false);
    }
  };

  // ─── toggle active ────────────────────────────────────────────
  const handleToggleActive = async (record) => {
    try {
      await updateGuidanceApi(record.id, { ...record, active: !record.active });
      message.success(record.active ? "Deactivated" : "Activated");
      fetchAll();
    } catch {
      message.error("Failed to update status");
    }
  };

  // ─── delete ───────────────────────────────────────────────────
  const handleDelete = (id) => {
    Modal.confirm({
      title: "Delete this content?",
      content: "This action cannot be undone.",
      okText: "Delete",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await deleteGuidanceApi(id);
          message.success("Deleted");
          fetchAll();
        } catch {
          message.error("Failed to delete");
        }
      },
    });
  };

  // ─── table columns ────────────────────────────────────────────
  const columns = [
    {
      title: "#",
      dataIndex: "id",
      width: 60,
      render: (v) => <span style={{ color: "#aaa", fontSize: 12 }}>#{v}</span>,
    },
    {
      title: "Title",
      dataIndex: "title",
      render: (v, r) => (
        <div>
          <div style={{ fontWeight: 600, color: "#111827" }}>{v}</div>
          {r.doctorName && (
            <div style={{ fontSize: 11, color: "#6366F1", marginTop: 2 }}>
              👨‍⚕️ {r.doctorName}{r.doctorSpeciality ? ` · ${r.doctorSpeciality}` : ""}
            </div>
          )}
          <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 2 }}>
            {r.body?.slice(0, 60)}{r.body?.length > 60 ? "…" : ""}
          </div>
        </div>
      ),
    },
    {
      title: "Age Group",
      dataIndex: "ageGroup",
      width: 130,
      render: (v) => v ? <Tag color="purple">{v}</Tag> : <span style={{ color: "#ccc" }}>—</span>,
    },
    {
      title: "Age Range",
      dataIndex: "ageRange",
      width: 110,
      render: (v) => v || <span style={{ color: "#ccc" }}>—</span>,
    },
    {
      title: "Sort",
      dataIndex: "sortOrder",
      width: 60,
      align: "center",
      render: (v) => <span style={{ color: "#6B7280", fontSize: 13 }}>{v}</span>,
    },
    {
      title: "Status",
      dataIndex: "active",
      width: 80,
      align: "center",
      render: (v) =>
        v ? (
          <Tag color="green">Active</Tag>
        ) : (
          <Tag color="default">Off</Tag>
        ),
    },
    {
      title: "Actions",
      width: 70,
      align: "center",
      render: (_, r) => (
        <Dropdown
          trigger={["click"]}
          menu={{
            items: [
              { key: "edit", label: "Edit", icon: <EditOutlined />, onClick: () => openEdit(r) },
              {
                key: "toggle",
                label: r.active ? "Deactivate" : "Activate",
                icon: <PoweroffOutlined />,
                onClick: () => handleToggleActive(r),
              },
              { type: "divider" },
              { key: "delete", label: "Delete", icon: <DeleteOutlined />, danger: true, onClick: () => handleDelete(r.id) },
            ],
          }}
        >
          <Button icon={<MoreOutlined />} size="small" />
        </Dropdown>
      ),
    },
  ];

  const activeCat = CATEGORIES.find((c) => c.value === activeTab);

  return (
    <div>
      {/* ── Page header ─────────────────────────────────────── */}
      <div className="page-header">
        <div>
          <div className="page-title">Guidance Content</div>
          <div className="page-sub">
            Manage doctor guidance, parenting tips and instructions
          </div>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          style={{ background: ACCENT, border: "none", borderRadius: 8 }}
          onClick={openAdd}
        >
          Add Content
        </Button>
      </div>

      {/* ── Category tabs ───────────────────────────────────── */}
      <div style={{ display: "flex", gap: 10, marginBottom: 20 }}>
        {CATEGORIES.map((cat) => {
          const isActive = activeTab === cat.value;
          return (
            <div
              key={cat.value}
              onClick={() => { setActiveTab(cat.value); setSearch(""); }}
              style={{
                cursor: "pointer",
                padding: "10px 20px",
                borderRadius: 10,
                border: `2px solid ${isActive ? cat.color : "#E5E7EB"}`,
                background: isActive ? cat.color + "12" : "#fff",
                display: "flex",
                alignItems: "center",
                gap: 8,
                transition: "all 0.2s",
                userSelect: "none",
              }}
            >
              <span style={{ color: isActive ? cat.color : "#9CA3AF", fontSize: 16 }}>
                {cat.icon}
              </span>
              <span
                style={{
                  fontWeight: 600,
                  fontSize: 14,
                  color: isActive ? cat.color : "#6B7280",
                }}
              >
                {cat.label}
              </span>
              <span
                style={{
                  marginLeft: 4,
                  fontSize: 11,
                  fontWeight: 700,
                  background: isActive ? cat.color : "#F3F4F6",
                  color: isActive ? "#fff" : "#9CA3AF",
                  borderRadius: 20,
                  padding: "1px 8px",
                }}
              >
                {countFor(cat.value)}
              </span>
            </div>
          );
        })}
      </div>

      {/* ── Search bar ──────────────────────────────────────── */}
      <div style={{ marginBottom: 16 }}>
        <Input.Search
          placeholder={`Search ${activeTab}…`}
          allowClear
          style={{ width: 320, borderRadius: 8 }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <span style={{ marginLeft: 12, fontSize: 13, color: "#9CA3AF" }}>
          {displayed.length} item{displayed.length !== 1 ? "s" : ""}
        </span>
      </div>

      {/* ── Table ───────────────────────────────────────────── */}
      <AppTable columns={columns} data={displayed} />

      {/* ── Add / Edit Modal ────────────────────────────────── */}
      <Modal
        title={
          <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ color: activeCat?.color }}>{activeCat?.icon}</span>
            {editing ? `Edit — ${editing.title}` : `Add ${activeTab}`}
          </span>
        }
        open={open}
        width={600}
        okText={editing ? "Save Changes" : "Add Content"}
        okButtonProps={{ style: { background: ACCENT, border: "none" }, loading }}
        onOk={handleSubmit}
        onCancel={() => { setOpen(false); setEditing(null); form.resetFields(); }}
        destroyOnClose
      >
        <Form form={form} layout="vertical" style={{ marginTop: 12 }}>
          {/* Category */}
          <AppSelect
            name="category"
            label="Category"
            rules={[{ required: true }]}
            options={CATEGORIES.map((c) => ({ value: c.value, label: c.label }))}
          />

          {/* Title */}
          <AppInput
            name="title"
            label="Title"
            rules={[{ required: true, message: "Title is required" }]}
            placeholder="e.g. Importance of Tummy Time"
          />

          {/* Age Group + Age Range side by side */}
          <div style={{ display: "flex", gap: 12 }}>
            <div style={{ flex: 1 }}>
              <AppSelect
                name="ageGroup"
                label="Age Group"
                options={AGE_GROUPS}
                placeholder="Select age group"
              />
            </div>
            <div style={{ flex: 1 }}>
              <AppInput
                name="ageRange"
                label="Age Range (optional)"
                placeholder="e.g. 6–9 months"
              />
            </div>
          </div>

          {/* Doctor fields — only for Doctor Guidance category */}
          {watchCategory === "Doctor Guidance" && (
            <div style={{ background: "#F5F3FF", borderRadius: 10, padding: "12px 16px", marginBottom: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#6366F1", marginBottom: 10 }}>
                👨‍⚕️ Doctor Attribution (optional)
              </div>
              <div style={{ display: "flex", gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <AppInput name="doctorName" label="Doctor Name" placeholder="e.g. Dr. Priya Sharma" />
                </div>
                <div style={{ flex: 1 }}>
                  <AppInput name="doctorSpeciality" label="Speciality" placeholder="e.g. Paediatric Nutrition" />
                </div>
              </div>
              <AppInput name="doctorPhotoUrl" label="Doctor Photo URL (optional)" placeholder="https://…" />
            </div>
          )}

          {/* Body */}
          <Form.Item
            name="body"
            label="Content / Description"
            rules={[{ required: true, message: "Content is required" }]}
          >
            <Input.TextArea
              rows={6}
              placeholder="Write the full guidance content here…"
              showCount
              maxLength={2000}
              style={{ borderRadius: 8, resize: "vertical" }}
            />
          </Form.Item>

          {/* Sort + Active side by side */}
          <div style={{ display: "flex", gap: 24, alignItems: "flex-start" }}>
            <Form.Item name="sortOrder" label="Sort Order" style={{ width: 120 }}>
              <InputNumber min={0} max={999} style={{ width: "100%", borderRadius: 8 }} />
            </Form.Item>
            <Form.Item name="active" label="Active" valuePropName="checked">
              <Switch checkedChildren="Yes" unCheckedChildren="No" />
            </Form.Item>
          </div>
        </Form>
      </Modal>
    </div>
  );
}
